/**
 * Compliance Trend card: complianceScore over time. Top contributing changes
 * are not yet provided by the backend (snapshots only store aggregate scores);
 * we show the trend and a short note. Backend gap: no per-category or
 * change-attribution API yet.
 */
import React, { useMemo } from 'react';
import { Card, CardHeader, CardBody } from '../ui';
import { SimpleLineChart } from './SimpleLineChart';

function getComplianceCaption(values) {
  if (!values.length) return 'Compliance score history will appear here as snapshots are recorded.';
  const last = values[values.length - 1];
  const first = values[0];
  const delta = last - first;
  if (delta > 5) return 'Your overall compliance score is trending up. Keep up the good work.';
  if (delta < -5) return 'Your score has dipped. Renewing documents or updating authority and vehicles can help.';
  return `Score is holding steady around ${Math.round(last)}. You're maintaining a solid baseline.`;
}

export default function ComplianceTrendCard({ snapshots, loading }) {
  const values = useMemo(() => {
    if (!snapshots?.length) return [];
    return snapshots
      .slice(0, 30)
      .map((s) => (s.complianceScore != null ? Number(s.complianceScore) : null))
      .filter((v) => v != null)
      .reverse();
  }, [snapshots]);
  const caption = useMemo(() => getComplianceCaption(values), [values]);

  if (loading) {
    return (
      <Card className="analytics-card compliance-trend-card">
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Loading trend...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="analytics-card compliance-trend-card">
      <CardHeader title="Compliance score trend" subtitle="DRI-style score over time" />
      <CardBody>
        <SimpleLineChart values={values} strokeColor="var(--amber)" caption={caption} />
        <p className="analytics-trend-note">
          Score contributors appear in &apos;What moved the needle&apos; as your compliance history builds.
        </p>
      </CardBody>
    </Card>
  );
}
